import { FileText, MoreHorizontal, Pencil } from "lucide-react";
import { Handle, Position, type Node, type NodeProps } from "@xyflow/react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { PageNodeData } from "./types";

export function PageNode({ data }: NodeProps<Node<PageNodeData>>) {
  const navigate = useNavigate();
  const { pageId, href, path, isEntry } = data;

  return (
    <div className="bg-white rounded-xl border-2 border-indigo-300 shadow-md w-[280px] relative pb-10">
      {!isEntry && (
        <Handle
          type="target"
          position={Position.Left}
          className="!bg-indigo-400 !w-3 !h-3 !border-2 !border-white"
          style={{ top: "22px", left: 0 }}
        />
      )}
      <div className="px-4 py-3 bg-indigo-50 rounded-t-xl border-b border-indigo-200">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <FileText className="h-4 w-4 shrink-0 text-indigo-500" />
            <span className="font-semibold text-sm text-indigo-700">Page</span>
          </div>
          {pageId ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="nodrag nopan pointer-events-auto h-7 w-7 shrink-0 text-indigo-600 hover:text-indigo-800"
                  aria-label="Page actions"
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="nodrag nopan pointer-events-auto"
                onPointerDown={(e) => e.stopPropagation()}
              >
                <DropdownMenuItem
                  onClick={() => navigate(`/pages/${pageId}/edit`)}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
        {href && path ? (
          <button
            type="button"
            className="nodrag nopan pointer-events-auto cursor-pointer relative z-10 mt-1.5 block max-w-full truncate text-left text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
            title={href}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              window.open(href, "_blank", "noopener,noreferrer");
            }}
          >
            {path}
          </button>
        ) : null}
      </div>
      <div className="absolute bottom-2 right-0 flex items-center">
        <span className="py-0.5 pr-2 rounded-md text-xs font-semibold text-indigo-600 bg-white/80">
          Submit
        </span>
        <Handle
          type="source"
          position={Position.Right}
          className="!bg-indigo-400 !w-3 !h-3 !border-2 !border-white"
        />
      </div>
    </div>
  );
}
